import React, { useState, useEffect } from 'react';
import { firebaseAuthService } from '../../services/googleServices';
import { initializeFirebase } from '../../config/firebase';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';

const GoogleLogin = ({ 
  onSuccess, 
  onError, 
  className = '',
  variant = 'button', // 'button' or 'icon'
  size = 'medium', // 'small', 'medium', 'large'
  disabled = false,
  showText = true
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { trackLogin, trackButtonClick } = useGoogleAnalytics();

  // Initialize Firebase on component mount
  useEffect(() => {
    const initFirebase = async () => {
      try {
        // Get Firebase config from backend
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/firebase/auth/config`);
        const data = await response.json();
        
        if (data.success && data.data) {
          const result = await initializeFirebase(data.data);
          setIsInitialized(result.success);
        }
      } catch (error) {
        console.error('Failed to initialize Firebase:', error);
        setIsInitialized(false);
      }
    };

    initFirebase();
  }, []);

  const handleGoogleLogin = async () => {
    if (disabled || isLoading || !isInitialized) return;

    setIsLoading(true);
    trackButtonClick('google_login_button');

    try {
      const result = await firebaseAuthService.signInWithGoogle();
      
      if (result.success) {
        // Store the authentication data directly
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        
        // Track successful login
        trackLogin(result.user.id, 'google');
        onSuccess && onSuccess(result.user);
        
        // Reload the page to update authentication state
        window.location.reload();
      } else {
        throw new Error(result.error || 'Google authentication failed');
      }
    } catch (error) {
      console.error('Google login error:', error);
      trackButtonClick('google_login_error');
      onError && onError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Size classes
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg'
  };

  // Icon sizes
  const iconSizes = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  if (!isInitialized) {
    return null; // Don't render if Firebase is not initialized
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleGoogleLogin}
        disabled={disabled || isLoading}
        className={`inline-flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${iconSizes[size]} ${className} ${
          disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
        }`}
        title="Sign in with Google"
      >
        {isLoading ? (
          <div className="animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 w-full h-full" />
        ) : (
          <svg className="w-full h-full" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleGoogleLogin}
      disabled={disabled || isLoading}
      className={`inline-flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-medium text-gray-700 dark:text-gray-300 ${sizeClasses[size]} ${className} ${
        disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
      }`}
    >
      {isLoading ? (
        <>
          <div className="animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 w-4 h-4 mr-2" />
          {showText && 'Signing in...'}
        </>
      ) : (
        <>
          <svg className={`${iconSizes[size]} mr-2`} viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {showText && 'Continue with Google'}
        </>
      )}
    </button>
  );
};

export default GoogleLogin;
